import React, { MouseEventHandler, forwardRef, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Icon,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  PopOut,
  RectCords,
  Text,
  config,
  toRem,
} from 'folds';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAtom, useAtomValue } from 'jotai';
import FocusTrap from 'focus-trap-react';
import { factoryRoomIdByActivity, factoryRoomIdByAtoZ } from '../../../utils/sort';
import {
  NavButton,
  NavCategory,
  NavCategoryHeader,
  NavEmptyCenter,
  NavEmptyLayout,
  NavItem,
  NavItemContent,
  NavLink,
  NavItemOptions,
} from '../../../components/nav';
import { getExplorePath, getHomeRoomPath, getHomeSearchPath } from '../../pathUtils';
import { getCanonicalAliasOrRoomId } from '../../../utils/matrix';
import { useSelectedRoom } from '../../../hooks/router/useSelectedRoom';
import { useHomeSearchSelected } from '../../../hooks/router/useHomeSelected';
import { useHomeRooms } from './useHomeRooms';
import { useMatrixClient } from '../../../hooks/useMatrixClient';
import { VirtualTile } from '../../../components/virtualizer';
import { RoomNavCategoryButton, RoomNavItem } from '../../../features/room-nav';
import { muteChangesAtom } from '../../../state/room-list/mutedRoomList';
import { makeNavCategoryId } from '../../../state/closedNavCategories';
import { roomToUnreadAtom } from '../../../state/room/roomToUnread';
import { useCategoryHandler } from '../../../hooks/useCategoryHandler';
import { useNavToActivePathMapper } from '../../../hooks/useNavToActivePathMapper';
import { openCreateRoom, openJoinAlias } from '../../../../client/action/navigation';
import { PageNav, PageNavHeader, PageNavContent } from '../../../components/page';
import { useRoomsUnread } from '../../../state/hooks/unread';
import { markAsRead } from '../../../../client/action/notifications';
import { useClosedNavCategoriesAtom } from '../../../state/hooks/closedNavCategories';
import { stopPropagation } from '../../../utils/keyboard';

// eslint-disable-next-line import/no-extraneous-dependencies
import "bootstrap-icons/font/bootstrap-icons.css";
import buildConfig from '../../../../../build.config';


import {
  SidebarStack,
  SidebarStackSeparator,
} from '../../../components/sidebar';

import {
  HomeTab,
  UserTab,
  InboxTab,
  SearchRoom,
} from '../sidebar';

import { InvitedSelectionAI, InvitedSelectionByRole } from '../../../features/room/InvitedSelection';
import personLogo from '../../../../../public/icon/新增真人按鈕.png';
import { RoomList, RoomListLieIdle } from '../../../features/room/RoomList';
import { MoreOperations } from './MoreOperations';

type HomeMenuProps = {
  requestClose: () => void;
};
const HomeMenu = forwardRef<HTMLDivElement, HomeMenuProps>(({ requestClose }, ref) => {
  const orphanRooms = useHomeRooms();
  const unread = useRoomsUnread(orphanRooms, roomToUnreadAtom);
  const mx = useMatrixClient();

  const handleMarkAsRead = () => {
    if (!unread) return;
    orphanRooms.forEach((rId) => markAsRead(mx, rId));
    requestClose();
  };

  const handleJoinAddress = () => {
    openJoinAlias();
    requestClose();
  };

  return (
    <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <MenuItem
          onClick={handleMarkAsRead}
          size="300"
          after={<Icon size="100" src={Icons.CheckTwice} />}
          radii="300"
          aria-disabled={!unread}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Mark as Read
          </Text>
        </MenuItem>
        <MenuItem
          onClick={handleJoinAddress}
          size="300"
          radii="300"
          after={<Icon size="100" src={Icons.Link} />}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Join with Address
          </Text>
        </MenuItem>
      </Box>
    </Menu>
  );
});

function HomeHeader() {
  const [menuAnchor, setMenuAnchor] = useState<RectCords>();

  const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    const cords = evt.currentTarget.getBoundingClientRect();
    setMenuAnchor((currentState) => {
      if (currentState) return undefined;
      return cords;
    });
  };

  return (
    <>
      <PageNavHeader>
        <Box alignItems="Center" grow="Yes" gap="300">
          <Box grow="Yes">
            <Text size="H4" truncate>
              Home
            </Text>
          </Box>
          <Box>
            <IconButton aria-pressed={!!menuAnchor} variant="Background" onClick={handleOpenMenu}>
              <Icon src={Icons.VerticalDots} size="200" />
            </IconButton>
          </Box>
        </Box>
      </PageNavHeader>
      <PopOut
        anchor={menuAnchor}
        position="Bottom"
        align="End"
        offset={6}
        content={
          <FocusTrap
            focusTrapOptions={{
              initialFocus: false,
              returnFocusOnDeactivate: false,
              onDeactivate: () => setMenuAnchor(undefined),
              clickOutsideDeactivates: true,
              isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
              isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
              escapeDeactivates: stopPropagation,
            }}
          >
            <HomeMenu requestClose={() => setMenuAnchor(undefined)} />
          </FocusTrap>
        }
      />
    </>
  );
}

function HomeEmpty() {
  const navigate = useNavigate();

  return (
    <NavEmptyCenter>
      <NavEmptyLayout
        icon={<Icon size="600" src={Icons.Hash} />}
        title={
          <Text size="H5" align="Center">
            No Rooms
          </Text>
        }
        content={
          <Text size="T300" align="Center">
            You do not have any rooms yet.
          </Text>
        }
        options={
          <>
            <Button onClick={() => openCreateRoom()} variant="Secondary" size="300">
              <Text size="B300" truncate>
                Create Room
              </Text>
            </Button>
            <Button
              onClick={() => navigate(getExplorePath())}
              variant="Secondary"
              fill="Soft"
              size="300"
            >
              <Text size="B300" truncate>
                Explore Community Rooms
              </Text>
            </Button>
          </>
        }
      />
    </NavEmptyCenter>
  );
}

const DEFAULT_CATEGORY_ID = makeNavCategoryId('home', 'room');
type HomeProps = { hasRoomSelected: boolean, setHasRoomSelected: React.Dispatch<React.SetStateAction<boolean>>, setRoomClosed: React.Dispatch<React.SetStateAction<boolean>> };
export function Home({hasRoomSelected, setHasRoomSelected, setRoomClosed}: HomeProps) {
  const mx = useMatrixClient();
  useNavToActivePathMapper('home');

  // const scrollRef = useRef<HTMLDivElement>(null);
  const rooms = useHomeRooms();
  // const muteChanges = useAtomValue(muteChangesAtom);
  // const mutedRooms = muteChanges.added;
  // const roomToUnread = useAtomValue(roomToUnreadAtom);

  const selectedRoomId = useSelectedRoom();
  const searchSelected = useHomeSearchSelected();
  const noRoomToDisplay = rooms.length === 0;
  // const [closedCategories, setClosedCategories] = useAtom(useClosedNavCategoriesAtom());
/*
  const sortedRooms = useMemo(() => {
    const items = Array.from(rooms).sort(
      closedCategories.has(DEFAULT_CATEGORY_ID)
        ? factoryRoomIdByActivity(mx)
        : factoryRoomIdByAtoZ(mx)
    );
    if (closedCategories.has(DEFAULT_CATEGORY_ID)) {
      return items.filter((rId) => roomToUnread.has(rId) || rId === selectedRoomId);
    }
    return items;
  }, [mx, rooms, closedCategories, roomToUnread, selectedRoomId]);

  const virtualizer = useVirtualizer({
    count: sortedRooms.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 38,
    overscan: 10,
  });

  const handleCategoryClick = useCategoryHandler(setClosedCategories, (categoryId) =>
    closedCategories.has(categoryId)
  );
*/
  useEffect(() => {
    if (selectedRoomId) setHasRoomSelected(true);
  }, [selectedRoomId]);

  return (
    <PageNav>
      {/* <HomeHeader /> */ }
      <SidebarStack>
        <UserTab />
        { /* <HomeTab /> */ }
      </SidebarStack>
      {/* noRoomToDisplay ? (
        <HomeEmpty />
      ) : ( */
        <PageNavContent >
          <Box direction="Column" gap="300">
            <NavCategory>
              {buildConfig.AISelection && (
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <InvitedSelectionAI sroomid={selectedRoomId} />
              </NavItem>
              )}
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <InvitedSelectionByRole sroomid={selectedRoomId} role="CS" icon={personLogo} label="客服" />
              </NavItem>
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <InvitedSelectionByRole sroomid={selectedRoomId} role="PM" icon={personLogo} label="PM" />
              </NavItem>
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <RoomList rooms={rooms} setRoomClosed={setRoomClosed} setHasRoomSelected={setHasRoomSelected} DEFAULT_CATEGORY_ID={DEFAULT_CATEGORY_ID}/>
              </NavItem>
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <RoomListLieIdle rooms={rooms} setRoomClosed={setRoomClosed} setHasRoomSelected={setHasRoomSelected} DEFAULT_CATEGORY_ID={DEFAULT_CATEGORY_ID}/>
              </NavItem>
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <NavButton onClick={() => openCreateRoom()}>
                  <NavItemContent>
                    <Box title="Create Room" as="span" grow="Yes" alignItems="Center" gap="200" style={{justifyContent: 'center'}}>
                      <Avatar size="200" radii="400">
                        <Icon src={Icons.Plus} size="100" />
                      </Avatar>
                      { /* <Box as="span" grow="Yes">
                        <Text as="span" size="Inherit" truncate>
                          Create Room
                        </Text>
                      </Box> */ }
                    </Box>
                  </NavItemContent>
                </NavButton>
              </NavItem>
              <NavItem variant="Background" radii="400" style={{justifyContent: 'center'}}>
                <SearchRoom />
              </NavItem>
              { /* <NavItem variant="Background" radii="400">
                <NavButton onClick={() => openJoinAlias()}>
                  <NavItemContent>
                    <Box title="Join with Address" as="span" grow="Yes" alignItems="Center" gap="200">
                      <Avatar size="200" radii="400">
                        <Icon src={Icons.Link} size="100" />
                      </Avatar>
                      { }
                    </Box>
                  </NavItemContent>
                </NavButton>
              </NavItem> */ }
              { /* <NavItem variant="Background" radii="400" aria-selected={searchSelected} style={{justifyContent: 'center'}}>
                <NavLink to={getHomeSearchPath()}>
                  <NavItemContent>
                    <Box title="Message Search" as="span" grow="Yes" alignItems="Center" gap="200" style={{justifyContent: 'center'}}>
                      <Avatar size="200" radii="400">
                        <Icon src={Icons.Search} size="100" filled={searchSelected} />
                      </Avatar>
                      { // <Box as="span" grow="Yes">
                        <Text as="span" size="Inherit" truncate>
                          Message Search
                        </Text>
                      </Box> }
                    </Box>
                  </NavItemContent>
                </NavLink>
              </NavItem> */ }
            </NavCategory>
            { /* <NavCategory>
              <NavCategoryHeader title="Rooms">
                <RoomNavCategoryButton
                  closed={closedCategories.has(DEFAULT_CATEGORY_ID)}
                  data-category-id={DEFAULT_CATEGORY_ID}
                  onClick={handleCategoryClick}
                >
                  Rooms
                </RoomNavCategoryButton>
              </NavCategoryHeader>
              <div
                style={{
                  position: 'relative',
                  height: virtualizer.getTotalSize(),
                }}
              >
                {virtualizer.getVirtualItems().map((vItem) => {
                  const roomId = sortedRooms[vItem.index];
                  const room = mx.getRoom(roomId);
                  if (!room) return null;
                  const selected = selectedRoomId === roomId;

                  return (
                    <VirtualTile
                      virtualItem={vItem}
                      key={vItem.index}
                      ref={virtualizer.measureElement}
                    >
                      <RoomNavItem
                        room={room}
                        selected={selected}
                        linkPath={getHomeRoomPath(getCanonicalAliasOrRoomId(mx, roomId))}
                        muted={mutedRooms.includes(roomId)}
                      />
                    </VirtualTile>
                  );
                })}
              </div>
            </NavCategory> */ }
            <NavCategory>
              <InboxTab />
            </NavCategory>
          </Box>
        </PageNavContent>
      /* ) */ }
      <SidebarStackSeparator />
      <SidebarStack>
        <MoreOperations />
      </SidebarStack>
    </PageNav>
  );
}
